import { use, useEffect, useState } from 'react';
import { FieldValues, Path, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import {
  getDevicesFromSystemForAddress,
  getLocalbusUnitPOEntryDeviceExternalId,
  getLocationFromDeviceExternalId,
  getUnitLocationDeviceExternalId,
} from 'shared/utils/IS32-utils';
import { stringToStringArray } from 'shared/utils/string-utils';

import { DeviceAlert } from 'features/device/domain/models/device-alert';
import { DeviceAlertType } from 'features/device/domain/models/device-alert-type';
import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { useIS32ContextProvider } from 'features/systems/IS32/context/IS32-provider';
import CreateIS32LocationDeviceCommand from 'features/systems/IS32/domain/models/create-IS32-device-command';
import DeleteIS32RoomUnitCommand from 'features/systems/IS32/domain/models/delete-IS32-room-unit-command';
import { IS32ConfigurationUnit } from 'features/systems/IS32/domain/models/IS32-configuration-unit';
import { IS32Event } from 'features/systems/IS32/domain/models/IS32-event';
import { IS32LocalBusUnit } from 'features/systems/IS32/domain/models/IS32-localbus-unit';
import UpdateIS32ConfigurationCommand from 'features/systems/IS32/domain/models/update-IS32-configuration-command';
import { useCreateIS32Device, useDeleteIS32RoomUnit, useUpdateIS32Configuration } from 'features/systems/IS32/domain/queries/IS32-queries';
import useIS32AccordionHook, { IS32AccordionHook } from 'features/systems/IS32/hooks/IS32-accordion-hook';
import useIS32OverflowMenuHook, { IS32OverflowMenuHook } from 'features/systems/IS32/hooks/IS32-overflow-menu-hook';
import { IS32OPEventType } from '../domain/models/IS32-OP-event-type';

export interface IS32DetailsHook {
  viewingMode: ViewingMode;
  cancelIS32ConfigurationMutation: () => void;
  selectedIS32ConfigurationUnit: IS32ConfigurationUnit | undefined;

  createIS32Device: (number: number, subUnitLocation?: string, localbusUnitReference?: number) => void;
  submitForm: (fieldValues: FieldValues) => void;
  updateIS32ConfigurationIsLoading: boolean;
  unitLocationHasDevice: (unitLocation: string, number?: number) => boolean;
  unitLocationHasLinkedDevice: (unitLocation: string, number?: number) => boolean;
  getUnitLocationDeviceBreadCrumbs: (unitLocation: string, number?: number) => string[];
  getDeviceAlertsForRoomUnit: (unitAddress: string) => DeviceAlert[];

  localbusUnitPOEntryHasDevice: (localbusUnitReference: number, poEntryIndex: number) => boolean;
  localbusUnitPOEntryHasLinkedDevice: (localbusUnitReference: number, poEntryIndex: number) => boolean;
  getLocalbusUnitPOEntryDeviceBreadCrumbs: (localbusUnitReference: number, poEntryIndex: number) => string[];
  getDeviceAlertsForLocalbusUnit: (unitAddress: string, reference: number) => DeviceAlert[];

  isConfigurationDetailsOpen: boolean;
  openConfigurationDetailsInEditingMode: () => void;

  deleteRoomUnit: () => void;
  isDeleteRoomUnitErrorPopupOpen: boolean;
  closeDeleteRoomUnitErrorPopup: () => void;

  deleteLocalbusUnit: (localbusUnitReference: number) => void;
  canDeleteLocalbusUnit: (unitAddress: string, localbusUnitReference: number) => boolean;
  revertDeleteLocalbusUnit: (localbusUnitReference: number) => void;
  localbusUnitIsDeleted: (localbusUnitReference: number) => boolean;

  configurationForm: UseFormReturn<Partial<IS32ConfigurationUnit>, any>;

  is32AccordionHook: IS32AccordionHook;
  is32OverflowMenuHook: IS32OverflowMenuHook;

  localbusUnitViewingMode: ViewingMode[];
  getLocalbusUnitViewingMode: (index: number) => ViewingMode;
  changeLocalbusUnitViewingMode: (index: number, mode: ViewingMode) => void;
  resetLocalbusUnitFormValues: (index: number) => void;
  setLocalbusUnitLocationValue: (index: number) => void;
  setLocalbusUnitEventsValue: (index: number) => void;
}

const useIS32DetailsHook = (unit: IS32ConfigurationUnit): IS32DetailsHook => {
  const { t } = useTranslation('IS32');
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { showConfirmationPopUp } = use(ConfirmationPopupProviderContext);

  const is32AccordionHook = useIS32AccordionHook();
  const is32OverflowMenuHook = useIS32OverflowMenuHook();

  const { refetch, openDeviceDetailsByDeviceId } = useIS32ContextProvider();
  const {
    systemsDetailsHook: { selectedSystem, refreshSystem },
  } = useSystemsContextProvider();

  const {
    mutate: updateIS32Configuration,
    isSuccess: updateIS32ConfigurationIsSuccess,
    isError: updateIS32ConfigurationIsError,
    isPending: updateIS32ConfigurationIsPending,
    error: updateIS32ConfigurationError,
    data: updateIS32ConfigurationData,
  } = useUpdateIS32Configuration();

  const {
    mutate: createIS32DeviceMutation,
    isSuccess: createIS32DeviceIsSuccess,
    isError: createIS32DeviceIsError,
    error: createIS32DeviceError,
    data: createIS32DeviceData,
  } = useCreateIS32Device();

  const {
    mutate: deleteIS32RoomUnit,
    isSuccess: deleteIS32RoomUnitIsSuccess,
    isError: deleteIS32RoomUnitIsError,
    error: deleteIS32RoomUnitError,
  } = useDeleteIS32RoomUnit();

  const [selectedIS32ConfigurationUnit, setSelectedIS32ConfigurationUnit] = useState<IS32ConfigurationUnit>(unit);
  const [isConfigurationDetailsOpen, setIsConfigurationDetailsOpen] = useState<boolean>(false);
  const [viewingMode, setViewingMode] = useState<ViewingMode>('viewing');
  const [localbusUnitViewingMode, setLocalbusUnitViewingMode] = useState<ViewingMode[]>([]);
  const [localbusUnitPreviousLocations, setLocalbusUnitPreviousLocations] = useState<string[][]>([]);
  const [localbusUnitPreviousEvents, setLocalbusUnitPreviousEvents] = useState<IS32Event[][]>([]);
  const [isDeleteRoomUnitErrorPopupOpen, setIsDeleteRoomUnitErrorPopupOpen] = useState(false);
  const [deletedLocalbusUnits, setDeletedLocalbusUnits] = useState<number[]>([]);

  useEffect(() => {
    setSelectedIS32ConfigurationUnit(unit);
  }, [unit]);

  const defaultValues: Partial<IS32ConfigurationUnit> = {
    room: selectedIS32ConfigurationUnit?.room ?? '',
    micLevel: selectedIS32ConfigurationUnit?.micLevel ?? null,
    speakerLevel: selectedIS32ConfigurationUnit?.speakerLevel ?? null,
    acousticMonitoringWait30Seconds: selectedIS32ConfigurationUnit?.acousticMonitoringWait30Seconds ?? null,
    resetInRoomForToilets: selectedIS32ConfigurationUnit?.resetInRoomForToilets ?? null,
    resetInRoomForBeds: selectedIS32ConfigurationUnit?.resetInRoomForBeds ?? null,
    onlyResetNoPresenceInToilets: selectedIS32ConfigurationUnit?.onlyResetNoPresenceInToilets ?? null,
    onlyResetNoPresenceInRoomBedsAndToilets: selectedIS32ConfigurationUnit?.onlyResetNoPresenceInRoomBedsAndToilets ?? null,
    assistanceToiletByPresenceInRoom: selectedIS32ConfigurationUnit?.assistanceToiletByPresenceInRoom ?? null,
    assistanceBedByPresenceInRoom: selectedIS32ConfigurationUnit?.assistanceBedByPresenceInRoom ?? null,
    nightFindLightsOffline: !selectedIS32ConfigurationUnit?.nightFindLightsOffline,
    firmwareVersion: selectedIS32ConfigurationUnit?.firmwareVersion ?? null,
    hardwareVersion: selectedIS32ConfigurationUnit?.hardwareVersion ?? null,
    note: selectedIS32ConfigurationUnit.note,
    localBusUnits: selectedIS32ConfigurationUnit?.localBusUnits,
  };

  const configurationForm = useForm({
    mode: 'onBlur',
    defaultValues,
  });

  const {
    reset,
    setValue,
    getValues,
    clearErrors,
    formState: { isDirty },
  } = configurationForm;

  useEffect(() => {
    resetConfigurationFormValues();
    setLocalbusUnitViewingMode(selectedIS32ConfigurationUnit?.localBusUnits?.map(() => 'viewing') ?? []);
    setLocalbusUnitPreviousLocations(selectedIS32ConfigurationUnit?.localBusUnits?.map((unit) => unit.locations ?? []) ?? []);
    setLocalbusUnitPreviousEvents(selectedIS32ConfigurationUnit?.localBusUnits?.map((unit) => unit.events ?? []) ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIS32ConfigurationUnit]);

  useEffect(() => {
    if (updateIS32ConfigurationIsSuccess && updateIS32ConfigurationData) {
      setSelectedIS32ConfigurationUnit(updateIS32ConfigurationData);
      closeConfigurationDetails(false);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIS32ConfigurationIsSuccess, updateIS32ConfigurationData]);

  useEffect(() => {
    if (updateIS32ConfigurationIsError && updateIS32ConfigurationError) {
      setErrorMessage(updateIS32ConfigurationError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIS32ConfigurationIsError, updateIS32ConfigurationData]);

  useEffect(() => {
    if (createIS32DeviceIsSuccess && createIS32DeviceData) {
      openDeviceDetailsByDeviceId(createIS32DeviceData.deviceId);
      refreshSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createIS32DeviceIsSuccess]);

  useEffect(() => {
    if (createIS32DeviceIsError && createIS32DeviceError) {
      setErrorMessage(createIS32DeviceError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createIS32DeviceIsError]);

  useEffect(() => {
    if (deleteIS32RoomUnitIsSuccess) {
      refreshSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteIS32RoomUnitIsSuccess]);

  useEffect(() => {
    if (deleteIS32RoomUnitIsError && deleteIS32RoomUnitError) {
      setErrorMessage(deleteIS32RoomUnitError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteIS32RoomUnitIsError]);

  const resetConfigurationFormValues = () => {
    reset({
      room: selectedIS32ConfigurationUnit?.room ?? '',
      micLevel: selectedIS32ConfigurationUnit?.micLevel ?? null,
      speakerLevel: selectedIS32ConfigurationUnit?.speakerLevel ?? null,
      acousticMonitoringWait30Seconds: selectedIS32ConfigurationUnit?.acousticMonitoringWait30Seconds ?? null,
      resetInRoomForToilets: selectedIS32ConfigurationUnit?.resetInRoomForToilets ?? null,
      resetInRoomForBeds: selectedIS32ConfigurationUnit?.resetInRoomForBeds ?? null,
      onlyResetNoPresenceInToilets: selectedIS32ConfigurationUnit?.onlyResetNoPresenceInToilets ?? null,
      onlyResetNoPresenceInRoomBedsAndToilets: selectedIS32ConfigurationUnit?.onlyResetNoPresenceInRoomBedsAndToilets ?? null,
      assistanceToiletByPresenceInRoom: selectedIS32ConfigurationUnit?.assistanceToiletByPresenceInRoom ?? null,
      assistanceBedByPresenceInRoom: selectedIS32ConfigurationUnit?.assistanceBedByPresenceInRoom ?? null,
      nightFindLightsOffline:
        selectedIS32ConfigurationUnit?.nightFindLightsOffline !== undefined &&
        selectedIS32ConfigurationUnit?.nightFindLightsOffline !== null
          ? !selectedIS32ConfigurationUnit?.nightFindLightsOffline
          : null,
      firmwareVersion: selectedIS32ConfigurationUnit?.firmwareVersion ?? null,
      hardwareVersion: selectedIS32ConfigurationUnit?.hardwareVersion ?? null,
      note: selectedIS32ConfigurationUnit?.note,
      localBusUnits:
        selectedIS32ConfigurationUnit?.localBusUnits?.map((unit) => {
          const defaultEvents: IS32Event[] = [
            (() => {
              const foundEvent0 = unit.events?.find((event) => event.number === 0);
              return foundEvent0
                ? {
                    ...foundEvent0,
                    eventTypeLow: foundEvent0.eventTypeLow ?? IS32OPEventType.NoEvent,
                    eventTypeHigh: foundEvent0.eventTypeHigh ?? IS32OPEventType.NoEvent,
                  }
                : {
                    reference: unit.reference,
                    number: 0,
                    eventTypeLow: IS32OPEventType.NoEvent,
                    eventTypeHigh: IS32OPEventType.NoEvent,
                  };
            })(),
            (() => {
              const foundEvent1 = unit.events?.find((event) => event.number === 1);
              return foundEvent1
                ? {
                    ...foundEvent1,
                    eventTypeLow: foundEvent1.eventTypeLow ?? IS32OPEventType.NoEvent,
                    eventTypeHigh: foundEvent1.eventTypeHigh ?? IS32OPEventType.NoEvent,
                  }
                : {
                    reference: unit.reference,
                    number: 1,
                    eventTypeLow: IS32OPEventType.NoEvent,
                    eventTypeHigh: IS32OPEventType.NoEvent,
                  };
            })(),
          ];

          return {
            ...unit,
            events: defaultEvents.filter(Boolean),
          };
        }) ?? [],
    });
  };

  const resetLocalbusUnitFormValues = (index: number): void => {
    const originalLocations = localbusUnitPreviousLocations[index];
    if (originalLocations !== undefined) {
      setValue(`localBusUnits.${index}.locations`, originalLocations);
      clearErrors(`localBusUnits.${index}.locations`);
    }

    const originalEvents = localbusUnitPreviousEvents[index];
    if (originalEvents !== undefined) {
      const entryIndices = [0, 1];
      entryIndices.forEach((poEntryIndex) => {
        const fieldNameLow: Path<Partial<IS32ConfigurationUnit>> = `localBusUnits.${index}.events.${poEntryIndex}.eventTypeLow`;
        const fieldNameHigh: Path<Partial<IS32ConfigurationUnit>> = `localBusUnits.${index}.events.${poEntryIndex}.eventTypeHigh`;
        const eventToReset = originalEvents.find((event) => event.number === poEntryIndex);
        setValue(fieldNameLow, eventToReset?.eventTypeLow);
        setValue(fieldNameHigh, eventToReset?.eventTypeHigh);
      });
    }
  };

  const submitForm = (fieldValues: FieldValues) => {
    if (!selectedIS32ConfigurationUnit) return;

    const transformedLocalBusUnits = fieldValues.localBusUnits?.map((unit: IS32LocalBusUnit) => ({
      ...unit,
      locations: stringToStringArray(unit.locations),
      events: unit.events?.map((event: IS32Event) => ({
        ...event,
        eventTypeLow: event.eventTypeLow === IS32OPEventType.NoEvent ? null : event.eventTypeLow,
        eventTypeHigh: event.eventTypeHigh === IS32OPEventType.NoEvent ? null : event.eventTypeHigh,
      })),
    }));

    const configuration: UpdateIS32ConfigurationCommand = {
      address: selectedIS32ConfigurationUnit.address,
      serial: selectedIS32ConfigurationUnit.serial,
      type: selectedIS32ConfigurationUnit.type,
      displayType: selectedIS32ConfigurationUnit.displayType,
      room: fieldValues.room.trim(),
      micLevel: fieldValues.micLevel,
      speakerLevel: fieldValues.speakerLevel,
      acousticMonitoringWait30Seconds: fieldValues.acousticMonitoringWait30Seconds,
      resetInRoomForToilets: fieldValues.resetInRoomForToilets,
      resetInRoomForBeds: fieldValues.resetInRoomForBeds,
      onlyResetNoPresenceInToilets: fieldValues.onlyResetNoPresenceInToilets,
      onlyResetNoPresenceInRoomBedsAndToilets: fieldValues.onlyResetNoPresenceInRoomBedsAndToilets,
      assistanceToiletByPresenceInRoom: fieldValues.assistanceToiletByPresenceInRoom,
      assistanceBedByPresenceInRoom: fieldValues.assistanceBedByPresenceInRoom,
      nightFindLightsOffline:
        fieldValues.nightFindLightsOffline !== undefined && fieldValues.nightFindLightsOffline !== null
          ? !fieldValues.nightFindLightsOffline
          : null,
      firmwareVersion: selectedIS32ConfigurationUnit.firmwareVersion,
      hardwareVersion: selectedIS32ConfigurationUnit.hardwareVersion,
      localBusUnits: transformedLocalBusUnits,
      systemId: selectedSystem?.id ?? '',
      note: fieldValues.note,
      localbusUnitRefsToDelete: deletedLocalbusUnits,
    };
    updateIS32Configuration(configuration);
  };

  const createIS32Device = (number: number, subUnitLocation?: string, localbusUnitReference?: number) => {
    const command: CreateIS32LocationDeviceCommand = {
      systemId: selectedSystem?.id ?? '',
      address: selectedIS32ConfigurationUnit.address,
      location: subUnitLocation,
      reference: localbusUnitReference,
      number: number,
    };

    createIS32DeviceMutation(command);
  };

  const openConfigurationDetailsInEditingMode = () => {
    setViewingMode('editing');
    setIsConfigurationDetailsOpen(true);
    resetConfigurationFormValues();
    setDeletedLocalbusUnits([]);
  };

  const cancelIS32ConfigurationMutation = () => {
    closeConfigurationDetails(isDirty || deletedLocalbusUnits.length > 0);
  };

  const closeConfigurationDetails = (isFormStateDirty: boolean) => {
    if (!isFormStateDirty) {
      closeDetailsPopup();
      return;
    }

    showConfirmationPopUp(
      () => closeDetailsPopup(),
      t('unsavedChangesConfirmationPopup.title'),
      t('unsavedChangesConfirmationPopup.body'),
      t('unsavedChangesConfirmationPopup.primaryButtonText'),
      t('unsavedChangesConfirmationPopup.secondaryButtonText'),
    );
  };

  const closeDetailsPopup = () => {
    resetConfigurationFormValues();
    setViewingMode('viewing');
    setIsConfigurationDetailsOpen(false);
    setLocalbusUnitViewingMode(selectedIS32ConfigurationUnit?.localBusUnits?.map(() => 'viewing') ?? []);
  };

  const getLocalbusUnitViewingMode = (index: number): ViewingMode => {
    return localbusUnitViewingMode[index] ?? 'viewing';
  };

  const changeLocalbusUnitViewingMode = (index: number, mode: ViewingMode) => {
    setLocalbusUnitViewingMode((prevModes) => prevModes.map((item, idx) => (idx === index ? mode : item)));
  };

  const setLocalbusUnitLocationValue = (index: number) => {
    const currentFormLocationsValue = getValues(`localBusUnits.${index}.locations`);

    const locationsToStoreAsPrevious = stringToStringArray(currentFormLocationsValue);

    setLocalbusUnitPreviousLocations((prevPreviousLocations) => {
      const newPreviousLocations = [...prevPreviousLocations];
      if (index >= 0 && index < newPreviousLocations.length) {
        newPreviousLocations[index] = locationsToStoreAsPrevious;
      }
      return newPreviousLocations;
    });
  };

  const setLocalbusUnitEventsValue = (index: number) => {
    const entryIndices = [0, 1];

    const currentEventsForUnit: IS32Event[] = entryIndices.map((poEntryIndex) => {
      const fieldNameLow: Path<Partial<IS32ConfigurationUnit>> = `localBusUnits.${index}.events.${poEntryIndex}.eventTypeLow`;
      const fieldNameHigh: Path<Partial<IS32ConfigurationUnit>> = `localBusUnits.${index}.events.${poEntryIndex}.eventTypeHigh`;

      const eventTypeHigh = getValues(fieldNameHigh) as IS32OPEventType;
      const eventTypeLow = getValues(fieldNameLow) as IS32OPEventType;

      return {
        reference: index,
        number: poEntryIndex,
        eventTypeLow: eventTypeLow,
        eventTypeHigh: eventTypeHigh,
      };
    });

    setLocalbusUnitPreviousEvents((prevPreviousEvents) => {
      const newPreviousEvents = [...prevPreviousEvents];
      if (index >= 0 && index < newPreviousEvents.length) {
        newPreviousEvents[index] = currentEventsForUnit;
      }
      return newPreviousEvents;
    });
  };

  const unitLocationHasDevice = (unitLocation: string, number?: number): boolean => {
    if (!selectedSystem?.macAddress) {
      return false;
    }
    const externalId = getUnitLocationDeviceExternalId(
      selectedSystem.macAddress,
      selectedIS32ConfigurationUnit?.address,
      unitLocation,
      number,
    );
    const deviceId = selectedSystem.devices?.find((device) => device.externalId === externalId)?.id;
    return !!deviceId;
  };

  const unitLocationHasLinkedDevice = (unitLocation: string, number?: number): boolean => {
    if (!unitLocationHasDevice(unitLocation, number)) {
      return false;
    }

    if (!selectedSystem?.macAddress) {
      return false;
    }
    const externalId = getUnitLocationDeviceExternalId(
      selectedSystem.macAddress,
      selectedIS32ConfigurationUnit?.address,
      unitLocation,
      number,
    );
    const deviceOrganisationUnitId = selectedSystem.devices?.find((device) => device.externalId === externalId)?.organisationUnitId;
    return !!deviceOrganisationUnitId;
  };

  const getUnitLocationDeviceBreadCrumbs = (unitLocation: string, number?: number): string[] => {
    if (!unitLocationHasDevice(unitLocation, number)) {
      return [];
    }

    if (!selectedSystem?.macAddress) {
      return [];
    }

    const externalId = getUnitLocationDeviceExternalId(
      selectedSystem.macAddress,
      selectedIS32ConfigurationUnit?.address,
      unitLocation,
      number,
    );
    const deviceBreadCrumbs = selectedSystem.devices?.find((device) => device.externalId === externalId)?.breadCrumbs;
    return deviceBreadCrumbs ?? [];
  };

  const localbusUnitPOEntryHasDevice = (localbusUnitReference: number, poEntryIndex: number): boolean => {
    if (!selectedSystem?.macAddress) {
      return false;
    }

    const externalId = getLocalbusUnitPOEntryDeviceExternalId(
      selectedSystem.macAddress,
      selectedIS32ConfigurationUnit?.address,
      localbusUnitReference,
      poEntryIndex,
    );

    const deviceId = selectedSystem.devices?.find((device) => device.externalId === externalId)?.id;
    return !!deviceId;
  };

  const localbusUnitPOEntryHasLinkedDevice = (localbusUnitReference: number, poEntryIndex: number): boolean => {
    if (!localbusUnitPOEntryHasDevice(localbusUnitReference, poEntryIndex)) {
      return false;
    }

    if (!selectedSystem?.macAddress) {
      return false;
    }
    const externalId = getLocalbusUnitPOEntryDeviceExternalId(
      selectedSystem.macAddress,
      selectedIS32ConfigurationUnit?.address,
      localbusUnitReference,
      poEntryIndex,
    );
    const deviceOrganisationUnitId = selectedSystem.devices?.find((device) => device.externalId === externalId)?.organisationUnitId;

    return !!deviceOrganisationUnitId;
  };

  const getLocalbusUnitPOEntryDeviceBreadCrumbs = (localbusUnitReference: number, poEntryIndex: number): string[] => {
    if (!localbusUnitPOEntryHasDevice(localbusUnitReference, poEntryIndex)) {
      return [];
    }

    if (!selectedSystem?.macAddress) {
      return [];
    }

    const externalId = getLocalbusUnitPOEntryDeviceExternalId(
      selectedSystem.macAddress,
      selectedIS32ConfigurationUnit?.address,
      localbusUnitReference,
      poEntryIndex,
    );
    const deviceBreadCrumbs = selectedSystem.devices?.find((device) => device.externalId === externalId)?.breadCrumbs;
    return deviceBreadCrumbs ?? [];
  };

  const deleteRoomUnit = () => {
    if (canDeleteRoomUnit(selectedIS32ConfigurationUnit)) {
      showConfirmationPopUp(
        () => {
          if (!selectedSystem) {
            return;
          }
          const command = {
            systemId: selectedSystem?.id ?? '',
            address: selectedIS32ConfigurationUnit.address,
          } as DeleteIS32RoomUnitCommand;
          deleteIS32RoomUnit(command);
        },
        t('deleteRoomUnitConfirmationPopup.title', {
          roomUnitName: selectedIS32ConfigurationUnit.room || t('deleteRoomUnitConfirmationPopup.roomUnit'),
        }),
        t('deleteRoomUnitConfirmationPopup.body'),
        t('deleteRoomUnitConfirmationPopup.confirmButton'),
        t('deleteRoomUnitConfirmationPopup.cancelButton'),
      );
    } else {
      setIsDeleteRoomUnitErrorPopupOpen(true);
    }
  };

  const canDeleteRoomUnit = (roomUnit: IS32ConfigurationUnit) => {
    if (selectedSystem === undefined) {
      return true;
    }

    const devices = getDevicesFromSystemForAddress(selectedSystem.devices, roomUnit.address);

    return !devices.some((device) => !!device.organisationUnitId);
  };

  const closeDeleteRoomUnitErrorPopup = () => setIsDeleteRoomUnitErrorPopupOpen(false);

  const deleteLocalbusUnit = (localbusUnitReference: number) => {
    showConfirmationPopUp(
      () => {
        if (!selectedSystem) {
          return;
        }

        setDeletedLocalbusUnits([...deletedLocalbusUnits, localbusUnitReference]);
      },
      t('deleteLocalbusUnitConfirmationPopup.title'),
      '',
      t('deleteLocalbusUnitConfirmationPopup.confirmButton'),
      t('deleteLocalbusUnitConfirmationPopup.cancelButton'),
    );
  };

  const canDeleteLocalbusUnit = (unitAddress: string, localbusUnitReference: number) => {
    if (selectedSystem === undefined) {
      return false;
    }
    const deviceAlerts = getDeviceAlertsForLocalbusUnit(unitAddress, localbusUnitReference);

    if (deviceAlerts.length > 0) {
      return deviceAlerts.some((alert) => alert.type === DeviceAlertType.IS32LocalbusError);
    }
    return false;
  };

  const localbusUnitIsDeleted = (localbusUnitReference: number) => {
    return deletedLocalbusUnits.includes(localbusUnitReference);
  };

  const revertDeleteLocalbusUnit = (localbusUnitReference: number) => {
    setDeletedLocalbusUnits((prev) => prev.filter((item) => item !== localbusUnitReference));
  };

  const getDeviceAlertsForRoomUnit = (unitAddress: string) => {
    if (!selectedSystem) {
      return [];
    }
    const devices = getDevicesFromSystemForAddress(selectedSystem.devices, unitAddress);

    return devices.flatMap((device) => {
      const location = getLocationFromDeviceExternalId(device.externalId);

      return (device.deviceAlerts || []).map((alert) => ({
        ...alert,
        location: location,
      }));
    });
  };

  const getDeviceAlertsForLocalbusUnit = (unitAddress: string, reference: number) => {
    if (!selectedSystem) {
      return [];
    }
    const devices = getDevicesFromSystemForAddress(selectedSystem.devices, unitAddress);

    return devices
      .flatMap((device) => {
        const location = getLocationFromDeviceExternalId(device.externalId);

        return (device.deviceAlerts || []).map((alert) => ({
          ...alert,
          location: location,
        }));
      })
      .filter((alert) => alert.reference === reference.toString());
  };

  return {
    viewingMode,
    cancelIS32ConfigurationMutation,
    selectedIS32ConfigurationUnit,

    createIS32Device,
    submitForm,
    updateIS32ConfigurationIsLoading: updateIS32ConfigurationIsPending,
    unitLocationHasDevice,
    unitLocationHasLinkedDevice,
    getUnitLocationDeviceBreadCrumbs,
    getDeviceAlertsForRoomUnit,

    localbusUnitPOEntryHasDevice,
    localbusUnitPOEntryHasLinkedDevice,
    getLocalbusUnitPOEntryDeviceBreadCrumbs,
    getDeviceAlertsForLocalbusUnit,

    isConfigurationDetailsOpen,
    openConfigurationDetailsInEditingMode,
    configurationForm,

    deleteRoomUnit,
    isDeleteRoomUnitErrorPopupOpen,
    closeDeleteRoomUnitErrorPopup,

    deleteLocalbusUnit,
    canDeleteLocalbusUnit,
    revertDeleteLocalbusUnit,
    localbusUnitIsDeleted,

    is32AccordionHook,
    is32OverflowMenuHook,

    localbusUnitViewingMode,
    getLocalbusUnitViewingMode,
    changeLocalbusUnitViewingMode,
    resetLocalbusUnitFormValues,
    setLocalbusUnitLocationValue,
    setLocalbusUnitEventsValue,
  };
};

export default useIS32DetailsHook;
